import { skedInboxApi } from "./_skedInboxApi";
import {
  Conversation,
  ConversationPost,
  Conversations,
  ConversationsFilters,
  ConversationType,
} from "types/conversationInbox";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";

export interface GetConversationsProps {
  next?: string;
  isStarred: boolean;
  isMentioned: boolean;
  sort?: string;
  accounts?: string[];
  assignedTo?: string | null;
  types: ConversationType[];
  status?: string | null;
  unassigned: boolean | null;
  ignoredAccountsComments: string[] | null;
  query?: string;
  sentiment?: string[];
  tags?: string[];
  dateFrom?: string;
  dateTo?: string;
  awaitingReply?: boolean;
  limit?: number;
}

export type GetConversationsResponse = {
  data: Conversations;
};

export async function getConversations({
  next,
  isStarred,
  isMentioned,
  sort,
  accounts,
  assignedTo,
  types,
  status,
  unassigned,
  ignoredAccountsComments,
  query,
  sentiment,
  tags,
  dateFrom,
  dateTo,
  awaitingReply,
  limit = 50,
}: GetConversationsProps): Promise<GetConversationsResponse> {
  const requestBody = omitBy(
    {
      next,
      limit,
      ...(isStarred ? { isStarred } : {}),
      ...(isMentioned ? { isMentioned } : {}),
      sort,
      accounts,
      assignedTo,
      type: types.filter((c) => c !== "COMMENT"), // COMMENT type is deprecated
      status,
      unassigned,
      ignoredAccountsComments,
      ...(query ? { query } : {}),
      ...(sentiment?.length ? { sentiment } : {}),
      ...(tags?.length ? { tags } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(awaitingReply ? { awaitingReply } : {}),
    },
    isNil
  );

  if ((accounts?.length || 0) > 200) {
    return await skedInboxApi.post("/conversations/bulk/get", requestBody);
  }

  return await skedInboxApi.get<Conversations>("/conversations", {
    params: requestBody,
  });
}

export async function getConversation({
  conversationId,
}: {
  conversationId: string;
}): Promise<{
  data: Conversation;
}> {
  return await skedInboxApi.get(`/conversations/${conversationId}`);
}

interface patchConversationProps {
  conversationId: string;
  status?: string;
  isStarred?: boolean;
  assignedTo?: string | null;
  unreadCount?: number;
}

export async function patchConversation({
  conversationId,
  status,
  isStarred,
  assignedTo,
  unreadCount,
}: patchConversationProps): Promise<{
  data: Conversation;
}> {
  return await skedInboxApi.patch(`/conversations/${conversationId}`, {
    status,
    isStarred,
    assignedTo,
    unreadCount,
  });
}

interface getConversationsCounterProps {
  accounts: string[];
  types: ConversationType[];
  ignoredAccountsComments: string[] | null;
}

export async function getConversationsCounter({
  accounts,
  types,
  ignoredAccountsComments,
}: getConversationsCounterProps): Promise<{
  data: ConversationsFilters & {
    open?: number;
    openCapped?: boolean | null;
  };
}> {
  const requestBody = omitBy(
    {
      accounts,
      type: types.filter((c) => c !== "COMMENT"), // COMMENT type is deprecated
      ignoredAccountsComments,
    },
    isNil
  );

  if ((accounts?.length || 0) > 200) {
    return await skedInboxApi.post("/conversations/bulk/counters", requestBody);
  }

  return await skedInboxApi.get("/conversations/counters", {
    params: requestBody,
  });
}

export async function deleteConversation(conversationId: string): Promise<{}> {
  return await skedInboxApi.delete(`/conversations/${conversationId}`);
}

export async function resyncInterlocutor({
  conversationId,
  interlocutorId,
}: {
  conversationId: string;
  interlocutorId: string;
}): Promise<{}> {
  return await skedInboxApi.put(
    `/conversations/${conversationId}/interlocutor/${interlocutorId}/resync`
  );
}

export async function closeAllConversation(accounts: string[]): Promise<void> {
  return await skedInboxApi.put(`/conversations/close-all`, {
    accounts,
  });
}

export interface patchConversationsProps {
  conversationIds: string[];
  status?: string;
  isStarred?: boolean;
  assignedTo?: string | null;
  unreadCount?: number;
}

export async function patchConversations({
  conversationIds,
  status,
  isStarred,
  assignedTo,
  unreadCount,
}: patchConversationsProps): Promise<{
  data: Conversation[];
}> {
  return await skedInboxApi.patch(`/conversations`, {
    ids: conversationIds,
    status,
    isStarred,
    assignedTo,
    unreadCount,
  });
}

export interface EscalateConversationResult {
  ticketId: string;
  ticketUrl: string;
  status: string;
}

// Escalate a conversation to an external destination (e.g. a support desk).
// `escalationId` is generated client-side (a uuid) so a double-submit / retry
// of the SAME escalation dedupes server-side — callers must pass the same id
// when retrying the same escalation, and a fresh id for a new one.
export async function escalateConversation(
  conversationId: string,
  {
    provider,
    escalationId,
    priority,
  }: { provider: string; escalationId: string; priority?: string }
): Promise<EscalateConversationResult> {
  const res = await skedInboxApi.post<EscalateConversationResult>(
    `/conversations/${conversationId}/escalate`,
    { provider, escalationId, ...(priority ? { priority } : {}) }
  );
  return res.data;
}

export async function resyncPostContent({
  postId,
  conversationId,
}: {
  postId: string;
  conversationId: string;
}): Promise<{
  data: ConversationPost;
}> {
  return await skedInboxApi.put(
    `/conversations/${conversationId}/posts/${postId}/resync`,
    {}
  );
}
